import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { Users, Plus, ArrowLeft, Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { TeamMemberForm } from '../../components/admin/TeamMemberForm';
import { TeamListSection } from '../../components/admin/TeamListSection';
import { pollSubtitleJob } from '../../utils/subtitles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_MEMBER = {
  name: '',
  role: '',
  bio: { en: '', ko: '', ja: '' },
  category: 'Management Team',
  video: '',
  subtitles: { ko: '', ja: '' },
  order: 0
};

const TeamManagement = () => {
  const { admin, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingSubtitle, setUploadingSubtitle] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [removingVideo, setRemovingVideo] = useState(false);
  const [removingSubtitle, setRemovingSubtitle] = useState(false);
  const [generatingSubtitles, setGeneratingSubtitles] = useState(false);
  const [formData, setFormData] = useState(EMPTY_MEMBER);
  const videoInputRef = useRef(null);

  const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
  const MAX_SUBTITLE_SIZE = 1 * 1024 * 1024; // 1MB

  // ==================== Data Fetching ====================

  const fetchTeamMembers = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/team-members`, { 
        withCredentials: true 
      });
      const members = response.data.members || [];
      setTeamMembers(members);
      // Migration banner removed - migration is a one-time operation done via API
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        toast.error('Failed to load team members');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!authLoading && !admin) { 
      navigate('/admin/login'); 
      return; 
    }
    if (admin) {
      fetchTeamMembers();
    }
  }, [admin, authLoading, navigate, fetchTeamMembers]);

  // ==================== CRUD Operations ====================

  const handleCreateNew = () => {
    const nextOrder = teamMembers.length > 0 
      ? Math.max(...teamMembers.map(m => m.order || 0)) + 1 
      : 1;
    setFormData({ ...EMPTY_MEMBER, order: nextOrder });
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (member) => {
    setFormData({
      name: member.name || '',
      role: member.role || '',
      bio: member.bio || { en: '', ko: '', ja: '' },
      category: member.category || 'Management Team',
      video: member.video_url || member.video || '',
      subtitles: member.subtitle_urls || member.subtitles || { ko: '', ja: '' },
      order: member.order || 0
    });
    setEditingId(member.id);
    setShowForm(true);
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/team-members/${memberId}`,
        { withCredentials: true }
      );
      toast.success('Team member deleted');
      fetchTeamMembers();
    } catch (error) {
      toast.error('Failed to delete team member');
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.role) {
      toast.error('Please fill in name and role');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        role: formData.role,
        category: formData.category,
        bio: formData.bio,
        video_url: formData.video,
        subtitle_urls: formData.subtitles,
        order: formData.order
      };

      if (editingId) {
        // Update existing member
        await axios.put(
          `${BACKEND_URL}/api/admin/team-members/${editingId}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Team member updated');
      } else {
        // Create new member
        await axios.post(
          `${BACKEND_URL}/api/admin/team-members`,
          payload,
          { withCredentials: true }
        );
        toast.success('Team member created');
      }

      setShowForm(false);
      setEditingId(null);
      setFormData(EMPTY_MEMBER);
      fetchTeamMembers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save team member';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(EMPTY_MEMBER);
  };

  // ==================== Upload Handlers ====================

  const handleVideoUpload = useCallback(async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }

    if (file.size > MAX_VIDEO_SIZE) {
      toast.error('Video file is too large. Maximum size is 100MB.');
      return;
    }

    setUploadingVideo(true);
    setUploadProgress(0);
    const uploadData = new FormData();
    uploadData.append('file', file);
    
    // Include old video URL to delete from storage (cleanup)
    if (formData.video && formData.video.startsWith('/api/media/videos/')) {
      uploadData.append('old_url', formData.video);
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload-video`, uploadData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      setFormData(currentFormData => ({ 
        ...currentFormData, 
        video: response.data.url 
      }));
      
      toast.success('Video uploaded successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to upload video';
      toast.error(errorMsg);
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  }, [formData.video]);

  const handleSubtitleUpload = async (e, langCode) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.vtt') && !file.name.endsWith('.srt')) {
      toast.error('Please select a VTT or SRT subtitle file');
      return;
    }

    if (file.size > MAX_SUBTITLE_SIZE) {
      toast.error('Subtitle file is too large. Maximum size is 1MB.');
      return;
    }

    setUploadingSubtitle(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    
    // Include old subtitle URL to delete from storage (cleanup)
    const oldSubtitleUrl = formData.subtitles?.[langCode];
    if (oldSubtitleUrl && oldSubtitleUrl.startsWith('/api/media/subtitles/')) {
      uploadData.append('old_url', oldSubtitleUrl);
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload-subtitle`, uploadData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({
        ...prev,
        subtitles: { ...prev.subtitles, [langCode]: response.data.url }
      }));
      toast.success(`${langCode.toUpperCase()} subtitle uploaded!`);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to upload subtitle';
      toast.error(errorMsg);
    } finally {
      setUploadingSubtitle(false);
      e.target.value = '';
    }
  };

  // ==================== Media Removal ====================

  const handleRemoveVideo = async () => {
    // For new members (not yet saved), just clear the form field
    if (!editingId) {
      setFormData(prev => ({ ...prev, video: '' }));
      return;
    }

    // For existing members, call API to delete from Object Store AND database
    setRemovingVideo(true);
    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/admin/team-members/${editingId}/video`,
        { withCredentials: true }
      );
      
      // Update form with returned member data
      if (response.data.member) {
        setFormData(prev => ({
          ...prev,
          video: response.data.member.video_url || '',
        }));
      }
      
      toast.success('Video removed successfully');
      fetchTeamMembers(); // Refresh list
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to remove video';
      toast.error(errorMsg);
    } finally {
      setRemovingVideo(false);
    }
  };

  const handleRemoveSubtitle = async (langCode) => {
    // For new members (not yet saved), just clear the form field
    if (!editingId) {
      setFormData(prev => ({
        ...prev,
        subtitles: { ...prev.subtitles, [langCode]: '' }
      }));
      return;
    }

    // For existing members, call API to delete from Object Store AND database
    setRemovingSubtitle(true);
    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/admin/team-members/${editingId}/subtitle/${langCode}`,
        { withCredentials: true }
      );
      
      // Update form with returned member data
      if (response.data.member) {
        setFormData(prev => ({
          ...prev,
          subtitles: {
            ...prev.subtitles,
            [langCode]: response.data.member.subtitle_urls?.[langCode] || ''
          }
        }));
      }
      
      toast.success(`${langCode.toUpperCase()} subtitle removed`);
      fetchTeamMembers(); // Refresh list
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to remove subtitle';
      toast.error(errorMsg);
    } finally {
      setRemovingSubtitle(false);
    }
  };

  // ==================== AI Subtitle Generation ====================

  const handleGenerateSubtitles = async () => {
    if (!editingId) {
      toast.error('Save the team member first, then generate subtitles');
      return;
    }
    setGeneratingSubtitles(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/team-members/${editingId}/generate-subtitles`,
        {},
        { withCredentials: true }
      );
      const job = await pollSubtitleJob('member', editingId);
      if (job.status === 'error') {
        toast.error(job.error || 'Subtitle generation failed');
      } else {
        const result = job.result || {};
        const generated = Object.keys(result.generated || {});
        const failed = Object.keys(result.failed || {});
        if (generated.length) {
          toast.success(`Subtitles generated: ${generated.map(l => l.toUpperCase()).join(', ')}`);
        } else {
          toast.info('All registered languages already have subtitles');
        }
        if (failed.length) {
          toast.error(`Failed: ${failed.map(l => l.toUpperCase()).join(', ')}`);
        }
        // Refresh form with the updated member
        const memberRes = await axios.get(
          `${BACKEND_URL}/api/admin/team-members/${editingId}`,
          { withCredentials: true }
        );
        if (memberRes.data.member) {
          setFormData(prev => ({
            ...prev,
            subtitles: memberRes.data.member.subtitle_urls || prev.subtitles
          }));
        }
      }
      fetchTeamMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Subtitle generation failed');
    } finally {
      setGeneratingSubtitles(false);
    }
  };

  // ==================== Order Management ====================

  const moveOrder = async (memberId, direction) => {
    const memberIndex = teamMembers.findIndex(m => m.id === memberId);
    if (memberIndex === -1) return;

    const newIndex = direction === 'up' ? memberIndex - 1 : memberIndex + 1;
    if (newIndex < 0 || newIndex >= teamMembers.length) return;

    // Create new order array
    const updated = [...teamMembers];
    const temp = updated[memberIndex];
    updated[memberIndex] = updated[newIndex];
    updated[newIndex] = temp;

    // Reassign order values
    const orders = updated.map((m, i) => ({ id: m.id, order: i + 1 }));

    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/team-members/reorder`,
        { orders },
        { withCredentials: true }
      );
      fetchTeamMembers();
    } catch (error) {
      toast.error('Failed to reorder');
    }
  };

  // ==================== Memoized Lists ====================

  const managementTeamList = useMemo(() => {
    return teamMembers
      .filter(m => m.category === 'Management Team')
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [teamMembers]);

  const teachersTeamList = useMemo(() => {
    return teamMembers
      .filter(m => m.category === "Teachers' Team")
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [teamMembers]);

  // ==================== Loading State ====================

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900" />
      </div>
    );
  }

  // ==================== Render ====================

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <Users className="w-6 h-6 text-slate-700" />
                <h1 className="text-xl font-bold text-slate-900">Team Management</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchTeamMembers}
                className="flex items-center gap-2 px-4 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                onClick={handleCreateNew}
                className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800"
                data-testid="create-team-member-btn"
              >
                <Plus className="w-4 h-4" />
                Add Member
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Empty State */}
        {teamMembers.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">No Team Members</h3>
            <p className="text-slate-500 mb-4">Get started by adding your first team member.</p>
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-lg hover:bg-slate-800"
            >
              <Plus className="w-5 h-5" />
              Add First Member
            </button>
          </div>
        )}

        {/* Team Lists */}
        {teamMembers.length > 0 && (
          <div className="space-y-8">
            <TeamListSection
              title="Management Team"
              members={managementTeamList}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onMove={(id, dir) => moveOrder(id, dir)}
            />
            <TeamListSection
              title="Teachers' Team"
              members={teachersTeamList}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onMove={(id, dir) => moveOrder(id, dir)}
            />
          </div>
        )}
      </main>

      {/* Team Member Form Modal */}
      {showForm && (
        <TeamMemberForm
          formData={formData}
          setFormData={setFormData}
          editingIndex={editingId}
          onSubmit={handleFormSubmit}
          onCancel={handleCancel}
          onVideoUpload={handleVideoUpload}
          onSubtitleUpload={handleSubtitleUpload}
          onRemoveVideo={handleRemoveVideo}
          onRemoveSubtitle={handleRemoveSubtitle}
          onGenerateSubtitles={handleGenerateSubtitles}
          generatingSubtitles={generatingSubtitles}
          uploadingVideo={uploadingVideo}
          uploadingSubtitle={uploadingSubtitle}
          uploadProgress={uploadProgress}
          removingVideo={removingVideo}
          removingSubtitle={removingSubtitle}
          videoInputRef={videoInputRef}
          saving={saving}
        />
      )}
    </div>
  );
};

export default TeamManagement;
